'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/progress'

interface ProgressBarProps {
  current: number
  total: number
  label?: string
  complete?: boolean
  className?: string
  indicatorClassName?: string
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  current,
  total,
  label = 'Progress',
  complete = false,
  className = '',
  indicatorClassName,
}) => {
  const safeTotal = Math.max(1, total)
  const clampedCurrent = Math.min(Math.max(current, 1), safeTotal)
  const normalized = (clampedCurrent - 1) / safeTotal
  const percentage = complete
    ? 100
    : Math.max(0, Math.min(100, normalized * 100))
  const rounded = Math.round(percentage)

  const defaultIndicator =
    '[&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-purple-600'

  return (
    <div className={cn('flex flex-col gap-1.5 sm:gap-2', className)}>
      {/* header row */}
      <div className="flex items-center justify-between text-[10px] sm:text-xs md:text-sm text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums">{rounded}%</span>
      </div>

      {/* bar */}
      <Progress
        value={percentage}
        aria-label={label}
        className={cn(
          'h-2.5 sm:h-3 w-full overflow-hidden',
          '[&>div]:transition-transform [&>div]:duration-500 [&>div]:ease-out [&>div]:will-change-transform',
          indicatorClassName ?? defaultIndicator,
        )}
      />
    </div>
  )
}

export default ProgressBar
